import { WeightUnit } from "@/Common/Utilities/Units/Units";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { BaseObstetricalFileForm } from "../BaseObstetricalFileForm";
import { ObstetricalFileMedicalSummary } from "../ObstetricalFileMedicalSummary";
import { ObstetricalFileVitalSign } from "../ObstetricalFileVitalSign";
import { ObstetricalHistoryItem } from "../ObstetricalHistoryItem";
import { BritishColumbiaObstetricalFileAllergiesMedications } from "./Part1/BritishColumbiaObstetricalFileAllergiesMedications";
import { BritishColumbiaObstetricalFileComments } from "./Part1/BritishColumbiaObstetricalFileComments";
import { BritishColumbiaObstetricalFileDatingEstimates } from "./Part1/BritishColumbiaObstetricalFileDatingEstimates";
import { BritishColumbiaObstetricalFileFamilyHistory } from "./Part1/BritishColumbiaObstetricalFileFamilyHistory";
import { BritishColumbiaObstetricalFileInitialPhysicalExamination } from "./Part1/BritishColumbiaObstetricalFileInitialPhysicalExamination";
import { BritishColumbiaObstetricalFileLifeStyleSocialConcerns } from "./Part1/BritishColumbiaObstetricalFileLifeStyleSocialConcerns";
import { BritishColumbiaObstetricalFileMedicalHistory } from "./Part1/BritishColumbiaObstetricalFileMedicalHistory";
import { BritishColumbiaObstetricalFileMetadata } from "./Part1/BritishColumbiaObstetricalFileMetadata";
import { BritishColumbiaObstetricalFilePatientInformation } from "./Part1/BritishColumbiaObstetricalFilePatientInformation";
import { BritishColumbiaObstetricalFilePresentPregnancy } from "./Part1/BritishColumbiaObstetricalFilePresentPregnancy";
import { BritishColumbiaObstetricalFileSubstanceUse } from "./Part1/BritishColumbiaObstetricalFileSubstanceUse";
import { BritishColumbiaObstetricalFileEdinburghPerinatalSection14 } from "./Part2/BritishColumbiaObstetricalFileEdinburghPerinatalSection14";
import { BritishColumbiaObstetricalFileInvestigations } from "./Part2/BritishColumbiaObstetricalFileInvestigations";
import { BritishColumbiaObstetricalFilePerinatalConsiderationsAndReferrals } from "./Part2/BritishColumbiaObstetricalFilePerinatalConsiderationsAndReferrals";
import { BritishColumbiaObstetricalFilePlannedPlaceOfBirth } from "./Part2/BritishColumbiaObstetricalFilePlannedPlaceOfBirth";
import { BritishColumbiaObstetricalFileSignOff } from "./Part2/BritishColumbiaObstetricalFileSignOff";
import { BritishColumbiaObstetricalFileUltrasound } from "./Part2/BritishColumbiaObstetricalFileUltrasound";
import { BritishColumbiaObstetricalVisit } from "./Part2/BritishColumbiaObstetricalVisit";
import { BritishColumbiaObstetricalFileFormResources } from "./Resources/BritishColumbiaObstetricalFileFormResources";

class BritishColumbiaObstetricalFileForm extends BaseObstetricalFileForm {
    public Section1: BritishColumbiaObstetricalFilePatientInformation;
    public Section2: BritishColumbiaObstetricalFileAllergiesMedications;
    public Section3: BritishColumbiaObstetricalFileDatingEstimates;
    public Section4: BritishColumbiaObstetricalFileObstetricalHistory;
    public Section5: BritishColumbiaObstetricalFilePresentPregnancy;
    public Section6: BritishColumbiaObstetricalFileFamilyHistory;
    public Section7: BritishColumbiaObstetricalFileMedicalHistory;
    public Section8: BritishColumbiaObstetricalFileLifeStyleSocialConcerns;
    public Section9: BritishColumbiaObstetricalFileSubstanceUse;
    public Section10: BritishColumbiaObstetricalFileInitialPhysicalExamination;
    public Section11: BritishColumbiaObstetricalFileComments;
    public Section12: BritishColumbiaObstetricalFilePlannedPlaceOfBirth;
    public Section13: BritishColumbiaObstetricalFileInvestigations;
    public Section14: BritishColumbiaObstetricalFileEdinburghPerinatalSection14;
    public Section15: BritishColumbiaObstetricalFileUltrasound[];
    public Section16: BritishColumbiaObstetricalFilePerinatalConsiderationsAndReferrals;
    public Section17: BritishColumbiaObstetricalVisit[];
    public Section18: BritishColumbiaObstetricalFileSignOff[];
    public Resources: BritishColumbiaObstetricalFileFormResources;

    public Metadata: BritishColumbiaObstetricalFileMetadata;

    public constructor(fields?: Partial<BritishColumbiaObstetricalFileForm>) {
        super(fields);
        Object.assign(this, fields);

        this.Section1 = new BritishColumbiaObstetricalFilePatientInformation(fields?.Section1);
        this.Section2 = new BritishColumbiaObstetricalFileAllergiesMedications(fields?.Section2);
        this.Section3 = new BritishColumbiaObstetricalFileDatingEstimates(fields?.Section3);
        this.Section4 = new BritishColumbiaObstetricalFileObstetricalHistory(fields?.Section4);
        this.Section5 = new BritishColumbiaObstetricalFilePresentPregnancy(fields?.Section5);
        this.Section6 = new BritishColumbiaObstetricalFileFamilyHistory(fields?.Section6);
        this.Section7 = new BritishColumbiaObstetricalFileMedicalHistory(fields?.Section7);
        this.Section8 = new BritishColumbiaObstetricalFileLifeStyleSocialConcerns(fields?.Section8);
        this.Section9 = new BritishColumbiaObstetricalFileSubstanceUse(fields?.Section9);
        this.Section10 = new BritishColumbiaObstetricalFileInitialPhysicalExamination(fields?.Section10);
        this.Section11 = new BritishColumbiaObstetricalFileComments(fields?.Section11);
        this.Section12 = new BritishColumbiaObstetricalFilePlannedPlaceOfBirth(fields?.Section12);
        this.Section13 = new BritishColumbiaObstetricalFileInvestigations(fields?.Section13);
        this.Section14 = new BritishColumbiaObstetricalFileEdinburghPerinatalSection14(fields?.Section14);
        this.Section15 = fields?.Section15?.map(p => new BritishColumbiaObstetricalFileUltrasound(p)) ?? [];
        this.Section16 = new BritishColumbiaObstetricalFilePerinatalConsiderationsAndReferrals(fields?.Section16);
        this.Section17 = fields?.Section17?.map(s => new BritishColumbiaObstetricalVisit(s)) ?? [];
        this.Section18 = fields?.Section18?.map(s => new BritishColumbiaObstetricalFileSignOff(s)) ??
            [
                new BritishColumbiaObstetricalFileSignOff(),
                new BritishColumbiaObstetricalFileSignOff(),
                new BritishColumbiaObstetricalFileSignOff()
            ];
        this.Resources = new BritishColumbiaObstetricalFileFormResources(fields?.Resources);

        this.Metadata = new BritishColumbiaObstetricalFileMetadata(fields?.Metadata);
    }

    public updateAgeAtEstimatedDateOfBirth(patientDateOfBirth: Date, estimatedDateOfBirth: Date): void {
        const ageAtEstimatedDateOfBirth = ObstetricsCalculator.calculateAgeAtEstimatedDateOfBirth(patientDateOfBirth, estimatedDateOfBirth);

        if (ageAtEstimatedDateOfBirth)
            this.Section1.AgeAtEstimatedDateOfDelivery = ageAtEstimatedDateOfBirth;
    }

    public updateSubsequentVisitGestationalAge(contributionId: string): void {
        const currentVisit = this.Section17.firstOrDefault(visit => visit.ContributionId == contributionId);
        const estimatedDateOfDelivery = this.Section12.ConfirmedEstimatedDateOfDelivery
            ?? this.Section3.EstimatedDateOfBirthByUltrasound
            ?? this.Section3.EstimatedDateOfBirthByLastMenstrualPeriod;

        if (currentVisit)
            currentVisit.GestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                estimatedDateOfDelivery,
                currentVisit.Date
            );

        if (this.Section14.Date)
            this.Section14.GestationalAge = ObstetricsCalculator.calculateWeeksOfGestation(
                estimatedDateOfDelivery,
                this.Section14.Date
            );
    }

    public setSubsequentVisitsVitalSigns(obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date, contributionCreationDate: Date, contributionId: string): void {
        const isVitalSignReused = this.isVitalSignReusedInPart2();
        this.Section17.forEach((visit, idx) => {
            const emrTemplateId = visit.EmrTemplateId;
            const vitalSign = ObstetricalFileVitalSign.createObstetricalFileVitalSign({
                obstetricalFileMedicalSummary: obstetricalFileMedicalSummary,
                emrTemplateId: emrTemplateId,
                consultationDate: consultationDate,
                obstetricalFileContributionDate: contributionCreationDate,
                skipLoadLastHour: this.shouldSkipLastHourVitalSign(idx, emrTemplateId, obstetricalFileMedicalSummary, consultationDate) || visit.ContributionId !== contributionId
            });

            // if the vital sign selected was a reuse from Section 10 then updates the Section 10 vital sign too
            if (isVitalSignReused && idx === 0)
                this.Section10.VitalSignEmrTemplateId = vitalSign?.emrTemplateId;

            visit.vitalSign = vitalSign;
            visit.EmrTemplateId = vitalSign?.emrTemplateId;
        });
    }

    public isVitalSignReusedInPart2(): boolean {
        return !!this.Section10.VitalSignEmrTemplateId
        && this.Section17?.any()
        && !!this.Section17[0].EmrTemplateId
        && this.Section17[0].EmrTemplateId === this.Section10.VitalSignEmrTemplateId;
    }

    private shouldSkipLastHourVitalSign(idx: number, emrTemplateId: string, obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary, consultationDate: Date): boolean {
        return (
            idx === 0 // if it is the first visit
            && !!this.Section10.VitalSignEmrTemplateId // if there is a vital sign selected in Part 1
            && (emrTemplateId == null || // if there is no selected vital sign for the current visit
                emrTemplateId != this.Section10.VitalSignEmrTemplateId || // if the vital sign selected for the current visit is not a reuse from Part 1
                obstetricalFileMedicalSummary.getVitalSignBy(emrTemplateId, consultationDate) !== null) // if the vital sign selected for the current visit is a reuse, but the vital sign doesn't exist anymore
        );
    }
}

class BritishColumbiaObstetricalFileObstetricalHistory {
    public ObstetricalHistoryMassUnit: WeightUnit;
    public ObstetricalHistory: ObstetricalHistoryItem[];

    public constructor(fields?: Partial<BritishColumbiaObstetricalFileObstetricalHistory>) {
        this.ObstetricalHistoryMassUnit = fields?.ObstetricalHistoryMassUnit ?? (sessionStore.useMetricSystemAsDefault ? WeightUnit.Gram : WeightUnit.Pound);
        this.ObstetricalHistory = (fields?.ObstetricalHistory ?? []).map(p => new ObstetricalHistoryItem(p));
    }
}

export { BritishColumbiaObstetricalFileForm };
